<template>
  <row>
    <column :lg="helpVisible? 8: 4">
      <div class="well well-lg">
        <template v-if="!helpVisible">
          <h1>Bienvenue sur votre interface d'administration Netexial&nbsp;!</h1>
          <hr />
          <p class="lead">
            Vous débutez dans la gestion des produits du catalogue&nbsp;?
          </p>
          <button class="btn btn-primary" @click="helpVisible = true">Suivez le guide&nbsp;!</button>
        </template>
        <template v-else>
          <h1>Petit guide pratique à l'usage des administrateurs</h1>

          <hr />

          <ul class="list-inline list-inline-dotted text-center">
            <li><a @click.prevent="helpSection = 0" href="#" :class="{'text-bold': !helpSection}">Structure générale</a></li>
            <li><a @click.prevent="helpSection = 1" href="#" :class="{'text-bold': helpSection === 1}">Couleurs</a></li>
            <li><a @click.prevent="helpSection = 2" href="#" :class="{'text-bold': helpSection === 2}">Produits et options</a></li>
            <li><a @click.prevent="helpSection = 3" href="#" :class="{'text-bold': helpSection === 3}">Groupes et combinaisons</a></li>
            <li><a @click.prevent="helpSection = 4" href="#" :class="{'text-bold': helpSection === 4}">Déclinaisons</a></li>
          </ul>

          <hr />

          <section v-show="!helpSection">
            <h5>1 ) Structure générale</h5>

            <p>
              Tous les produits sont regroupés au sein de <strong>collections</strong>. Chaque collection peut apparaître dans un <strong>métier</strong>.
              Pour pouvoir commencer à renseigner des produits, il faut donc avant tout qu'ils soient dans une arborescence de collections et de métiers.
            </p>
            <p>
              Dans un premier temps, il vous faudra vous rendre sur la page de <router-link :to="{name: 'categories'}" target="_blank">Gestion des métiers</router-link>,
              muni(e) de la liste ordonnée des métiers et des fichiers image des icônes de chaque métier.
            </p>
            <p>
              Une fois que les métiers sont renseignés, il est possible de continuer avec la <router-link :to="{name: 'collections'}" target="_blank">Gestion des collections</router-link>.
              Dans cette partie, vous aurez besoin des informations de chaque collection, notamment&nbsp;:
            </p>
            <ul>
              <li>le nom et le slogan,</li>
              <li>le métier associé,</li>
              <li>les images qui seront utilisées comme image d'accueil, de menu, de page <em>En savoir +</em> et pour la fiche collection,</li>
              <li>les informations liées aux risques et aux normes de sécurité.</li>
            </ul>

            <hr />

            <button class="btn btn-primary" @click="helpSection = 1">Suivant&nbsp;&raquo;</button>
          </section>

          <section v-show="helpSection === 1">
            <h5>2 ) Couleurs</h5>

            <p>
              Cette partie est certes rébarbative, mais elle est indispensable afin de pouvoir renseigner les produits.
              Il vous faudra vous rendre sur la <router-link :to="{name: 'colors'}" target="_blank">Gestion des couleurs</router-link> et renseigner
              l'ensemble des couleurs disponibles sur les produits que vous souhaitez ajouter.
            </p>
            <p>
              Pour cela, munissez-vous de la liste des couleurs existantes, simples ou bicolores, et si possible <strong>organisée selon les collections ou produits</strong> dans lesquelles elles sont utilisées.
              Pour chaque couleur, renseignez le nom et un nom de groupe qui vous permettra de facilement retrouver une couleur et affecter un groupe de couleurs.
            </p>
            <p>
              Par exemple, si vous avez une série de couleurs utilisées pour quasiment tous les produits d'une collection, utilisez comme nom de groupe cette collection.
            </p>
            <p>
              <strong>Attention&nbsp;: si 2 collections ont aujourd'hui la même couleur (même nom et codes couleurs), il peut être important de créer une couleur distincte pour chaque collection.</strong>
              Cela permettra d'accélerer le processus de création des produits et de faciliter le travail au cas où une des 2 versions serait amenée à être modifiée à long terme.
            </p>

            <p>Quand tout a été renseigné, il est désormais plus rapide de gérer tous les produits.</p>

            <hr />

            <button class="btn btn-primary" @click="helpSection = 2">Suivant&nbsp;&raquo;</button>
          </section>

          <section v-show="helpSection === 2">
            <h5>3 ) Produits et options</h5>

            <p>
              L'interface propose 4 types de produits&nbsp;: les vêtements, les linges, les tapis et les accessoires. Pour chacun le principe est le même&nbsp;: il faut paramétrer les informations
              générales du produit, notamment son affichage et les propriétés (attributs) possibles, puis compléter les <strong>déclinaisons</strong>, c'est-à-dire les références différentes en fonction de la couleur,
              la taille, etc.
            </p>

            <p>
              La décomposition d'un produit et de ses déclinaisons est fortement liée à la manière dont on souhaite afficher le produit dans l'interface. Il existe 2 grandes stratégies&nbsp;:
            </p>
            <ol>
              <li>organiser un produit comme un ensemble d'options ;</li>
              <li>créer une multitude de produits comme des déclinaisons, avec un minimum d'options pour chacun.</li>
            </ol>

            <p>Prenons comme exemple une veste avec les options suivantes&nbsp;:</p>
            <ul>
              <li>la couleur générale, qui est limitée à quelques couleurs de la collection&nbsp;;</li>
              <li>la couleur du liseré, parmi 2 ou 3 options possibles&nbsp;;</li>
              <li>la matière&nbsp;;</li>
              <li>le sexe&nbsp;;</li>
              <li>la présence ou non de poches.</li>
            </ul>

            <p>
              La première possibilité est de <strong>créer un seul produit</strong> &laquo;&nbsp;Veste&nbsp;&raquo; qui contiendra les 5 options.
              En <router-link :to="{name: 'product/create'}" target="_blank">créant le produit</router-link> et en sélectionnant le type de produit
              &laquo;&nbsp;Vêtement&nbsp;&raquo;, une liste de propriétés est pré-remplie. Il suffit de supprimer les propriétés non nécessaires et de rajouter
              une propriété &laquo;&nbsp;Poches&nbsp;&raquo; pour avoir&nbsp;:
            </p>
            <ul>
              <li>Sexe&nbsp;: choix parmi &laquo;&nbsp;Homme&nbsp;&raquo; et &laquo;&nbsp;Femme&nbsp;&raquo;&nbsp;;</li>
              <li>Matière&nbsp;: choix parmi &laquo;&nbsp;CR&nbsp;&raquo; et &laquo;&nbsp;PC&nbsp;&raquo;&nbsp;;</li>
              <li>Couleur&nbsp;: options choisies parmi celles possibles dans les déclinaisons&nbsp;;</li>
              <li>Liseré&nbsp;: options choisies parmi celles possibles dans les déclinaisons&nbsp;;</li>
              <li><strong>Poches&nbsp;: choix parmi &laquo;&nbsp;Avec&nbsp;&raquo; et &laquo;&nbsp;Sans&nbsp;&raquo;.</strong></li>
            </ul>
            <p>
              Avec ce système, l'utilisateur choisit parmi les vestes avec poches ou sans poches en tant qu'option, au même titre que la matière ou la couleur.
            </p>

            <p>
              Une deuxième possibilité est de <strong>créer un produit par variante</strong>, par exemple&nbsp;: &laquo;&nbsp;Veste CR avec poche&nbsp;&raquo;,
              &laquo;&nbsp;Veste CR sans poche&nbsp;&raquo;, &laquo;&nbsp;Veste PC&nbsp;&raquo;&hellip; Dans ce cas, on réduit le nombre de propriétés pour chaque
              produit, ce qui peut donner par exemple&nbsp;:
            </p>
            <ul>
              <li>Sexe&nbsp;: choix parmi &laquo;&nbsp;Homme&nbsp;&raquo; et &laquo;&nbsp;Femme&nbsp;&raquo;&nbsp;;</li>
              <li>Couleur&nbsp;: options choisies parmi celles possibles dans les déclinaisons&nbsp;;</li>
              <li>Liseré&nbsp;: options choisies parmi celles possibles dans les déclinaisons&nbsp;;</li>
            </ul>
            <p>
              En décomposant de cette manière, l'utilisateur choisit entre différents produits plutôt que différentes options. Il n'y a pas de choix meilleur qu'un
              autre, il faut néanmoins privilégier la compréhension et la cohérence globale pour l'utilisateur.
            </p>

            <p>
              Enfin, en plus des propriétés on définit pour chaque produit un ensemble d'images qui seront utilisées dans les déclinaisons. Cela permet de sélectionner
              une image existante pour plusieurs déclinaisons plutôt que de télécharger plusieurs fois la même image.
            </p>

            <hr />

            <button class="btn btn-primary" @click="helpSection = 3">Suivant&nbsp;&raquo;</button>
          </section>

          <section v-show="helpSection === 3">
            <h5>4 ) Groupes et combinaisons</h5>

            <p>
              En décomposant les produits en plusieurs, ou bien en voulant associer des produits entre eux, on fait appel au concept de groupes / combinaisons.
              Cela permet de <strong>regrouper en une entité nommée des produits</strong>.
            </p>
            <p>
              Le premier cas d'utilisation est celui des combinaisons&nbsp;: par exemple pouvoir configurer comme une seule entité une partie haute et une partie basse.
              Pour cela, dans <router-link :to="{name: 'product/create'}" target="_blank">l'édition ou la création d'un produit</router-link> il suffit de cocher la case
              &laquo;&nbsp;Ce produit fait partie d'une combinaison / d'un groupe&nbsp;&raquo;. Cela fait apparaître une liste déroulante ainsi qu'un bouton de gestion.
              Il est ensuite possible d'ajouter une combinaison (ex : &laquo;&nbsp;Haut + Bas&nbsp;&raquo;) et de renseigner les parties qui la composent (ex : &laquo;&nbsp;Haut&nbsp;&raquo;,
              &laquo;&nbsp;Bas&nbsp;&raquo;). En associant ensuite le produit à une des parties de la combinaison, le configurateur présentera la combinaison comme un
              point d'entrée à part au même niveau que les produits qui ne font pas partie d'une combinaison, et les produits qui la composent seront regroupées selon leurs parties.
            </p>
            <p>
              Mais puisque la combinaison est une entrée du même niveau que les produits, et puisqu'aussi lorsqu'il n'y a qu'une seule partie dans une combinaison son intitulé n'est pas affiché,
              il est possible d'utiliser ce système pour <strong>grouper les produits</strong>.
            </p>
            <p>
              Ce deuxième cas d'utilisation fait écho à la décomposition des produits en fonction de leurs options (exemple 2 vu précédemment).
              En supposant que 3 produits &laquo;&nbsp;Veste CR avec poche&nbsp;&raquo;, &laquo;&nbsp;Veste CR sans poche&nbsp;&raquo;, &laquo;&nbsp;Veste PC&nbsp;&raquo; ont
              été créés, le problème principal qui se pose est qu'ils seront affichés en haut de page au même niveau. Il est possible d'utiliser les groupes pour
              améliorer l'affichage pour l'utilisateur.
            </p>
            <p>
              Pour cela, toujours dans <router-link :to="{name: 'product/create'}" target="_blank">l'édition ou la création d'un produit</router-link> on va créer un groupe spécial
              qu'on nommera &laquo;&nbsp;Veste&nbsp;&raquo;, avec une seule partie &laquo;&nbsp;Toutes&nbsp;&raquo; (comme ce nom ne s'affichera pas dans l'interface, il n'a pas d'importance).
              Ensuite, il faudra affecter nos 3 produits à ce groupe. Dans l'interface, cela permettra d'avoir les 3 produits regroupés au sein d'un produit virtuel (le groupe) nommé
              proprement &laquo;&nbsp;Veste&nbsp;&raquo;. Puis, en choisissant ce produit, l'utilisateur a ensuite le choix entre les 3 versions de cette veste.
            </p>

            <hr />

            <button class="btn btn-primary" @click="helpSection = 4">Suivant&nbsp;&raquo;</button>
          </section>
          
          <section v-show="helpSection === 4">
            <h5>5 ) Déclinaisons</h5>

            <p>
              Une fois que les produits sont correctement paramétrés et rangés dans leurs groupes ou combinaisons, il reste l'essentiel&nbsp; renseigner les déclinaisons.
            </p>
            <p>
              Pour accéder à l'interface de gestion des déclinaisons d'un produit, il est possible de cliquer sur le nombre de déclinaisons du produit dans la liste, de cliquer sur
              l'action &laquo;&nbsp;Gérer les déclinaisons&nbsp;&raquo; du produit dans la liste, ou bien encore sur le bouton &laquo;&nbsp;Gérer les déclinaisons&nbsp;&raquo;
              en haut de page de l'édition d'un produit.
            </p>
            <p>
              Il est possible d'ajouter chaque déclinaison une par une, mais l'option la plus rapide est de <strong>générer des déclinaisons</strong> par lot.
              Pour cela, un formulaire s'ouvre et permet de choisir les valeurs qui seront déclinées. Par exemple, en choisissant &laquo;&nbsp;Homme&nbsp;&raquo;, &laquo;&nbsp;Femme&nbsp;&raquo;
              pour la propriété &laquo;&nbsp;Sexe&nbsp;&raquo; et &laquo;&nbsp;Bleu&nbsp;&raquo;, &laquo;&nbsp;Rouge&nbsp;&raquo;, &laquo;&nbsp;Vert&nbsp;&raquo; pour la propriété &laquo;&nbsp;Couleur&nbsp;&raquo;,
              6 déclinaisons (2 &times; 3) seront générées dans la liste. Il est ensuite plus facile de renseigner pour chacune leur référence, description et de sélectionner
              l'image qui correspond.
            </p>
            <p>
              Le générateur de déclinaisons permet également de nettoyer des déclinaisons qui ne seraient plus valides (ex : couleur plus disponible). En cochant les cases qui s'affichent dans le cas
              ou des déclinaisons obsolètes sont détectées, ces dernières sont enlevées et ne polluent plus l'interface.
            </p>

            <p>
              Une fois les déclinaisons créées, paramétrées, publiées au sein d'un produit dans une collection publiés, l'utilisateur y a accès et peut le configurer et l'ajouter à son panier.
              <strong>Le tour est joué&nbsp;!</strong>
            </p>

            <hr />

            <button class="btn btn-primary" @click="helpVisible = false">J'ai tout compris&nbsp;!</button>
          </section>
        </template>
      </div>
    </column>
    <column :lg="helpVisible? 4: 8">
      <panel light>
        <div v-if="$apollo.loading">
          <h4>Chargement&hellip;</h4>
        </div>
        <row v-else>
          <column :lg="helpVisible? 12: 4" v-for="collection in collections" :key="collection.id" class="text-center">
            <div class="Collection">
              <h4>
                <router-link :to="{name: 'collection/edit', params: {id: collection.id}}">
                  <i class="icon-pencil icon-left" />
                </router-link>
                {{collection.name}}
              </h4>
              <div>
                <router-link :to="{name: 'products', query: {collection: collection.id}}">
                  {{collection.products.length}} produit(s)
                </router-link>
              </div>
            </div>
          </column>
        </row>
      </panel>
    </column>
  </row>
</template>

<style>
.Collection {
  border-bottom: 1px solid #e0f2f1;
  margin-bottom: 20px;
  padding-bottom: 20px;
}
.Collection h4 {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<script>
import _map from "lodash/map";
import { AppMixin } from "../mixins/app-mixin";

import CollectionListQuery from "./collections/graphql/CollectionListQuery.gql";

export default {
  mixins: [AppMixin],
  components: {},
  data() {
    return {
      collections: [],
      helpVisible: false,
      helpSection: 0
    };
  },
  apollo: {
    collections: {
      query: CollectionListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            limit: 999
          }
        };
      },
      update: data => _map(data.results.edges, "node")
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/pages/Home.vue